import React, { useState, useEffect } from 'react';
import {
  Camera,
  CheckCircle,
  XCircle,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  Calendar,
  ExternalLink,
  Download,
  RefreshCw,
  Play,
  ZoomIn,
  X,
  FileText,
  Globe,
  Mail,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface Screenshot {
  id: string;
  name: string;
  path: string;
  url: string;
  timestamp: string;
  category: string;
  description: string;
  viewport?: { width: number; height: number };
  status?: 'pass' | 'fail' | 'pending';
  testName?: string;
}

interface TestRun {
  id: string;
  name: string;
  timestamp: string;
  duration: string;
  totalTests: number;
  passed: number;
  failed: number;
  screenshots: Screenshot[];
}

export const TestResultsGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<Screenshot | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Mock data for demonstration - in production, this would come from an API
  const mockScreenshots: Screenshot[] = [
    // Homepage screenshots
    {
      id: 'home-1',
      name: 'Homepage Desktop',
      path: 'homepage-desktop-2025-09-25.png',
      url: '/screenshots/homepage-desktop-2025-09-25.png',
      timestamp: '2025-09-25T10:12:00Z',
      category: 'homepage',
      description: 'Main homepage on desktop viewport',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Homepage Load Test'
    },
    {
      id: 'home-2',
      name: 'Homepage Mobile',
      path: 'homepage-mobile-2025-09-25.png',
      url: '/screenshots/homepage-mobile-2025-09-25.png',
      timestamp: '2025-09-25T10:12:05Z',
      category: 'homepage',
      description: 'Main homepage on mobile viewport',
      viewport: { width: 375, height: 812 },
      status: 'pass',
      testName: 'Mobile Responsiveness Test'
    },
    {
      id: 'home-3',
      name: 'Homepage Tablet',
      path: 'homepage-tablet-2025-09-25.png',
      url: '/screenshots/homepage-tablet-2025-09-25.png',
      timestamp: '2025-09-25T10:12:10Z',
      category: 'homepage',
      description: 'Main homepage on tablet viewport',
      viewport: { width: 768, height: 1024 },
      status: 'pass',
      testName: 'Tablet Responsiveness Test'
    },

    // Email test screenshots
    {
      id: 'email-1',
      name: 'Email Test Initial',
      path: 'email-live-initial.png',
      url: '/screenshots/email-live-initial.png',
      timestamp: '2025-09-25T17:33:00Z',
      category: 'email',
      description: 'Email feedback test page initial state',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Email Feedback Test'
    },
    {
      id: 'email-2',
      name: 'Email Test After Send',
      path: 'email-live-after-send.png',
      url: '/screenshots/email-live-after-send.png',
      timestamp: '2025-09-25T17:33:05Z',
      category: 'email',
      description: 'Email test after sending message',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Email Send Test'
    },
    {
      id: 'email-3',
      name: 'Email Test Final',
      path: 'email-live-final.png',
      url: '/screenshots/email-live-final.png',
      timestamp: '2025-09-25T17:33:10Z',
      category: 'email',
      description: 'Email test final state with results',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Email Flow Complete'
    },

    // Feature pages
    {
      id: 'feature-1',
      name: 'Philosophy Page',
      path: 'philosophy-2025-09-25.png',
      url: '/screenshots/philosophy-2025-09-25.png',
      timestamp: '2025-09-25T10:12:15Z',
      category: 'features',
      description: 'Philosophy page full capture',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Feature Pages Test'
    },
    {
      id: 'feature-2',
      name: 'Showcase Page',
      path: 'showcase-2025-09-25.png',
      url: '/screenshots/showcase-2025-09-25.png',
      timestamp: '2025-09-25T10:13:00Z',
      category: 'features',
      description: 'Showcase page with all features',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Feature Pages Test'
    },

    // Auth pages
    {
      id: 'auth-1',
      name: 'Auth Page',
      path: 'auth-page-2025-09-25.png',
      url: '/screenshots/auth-page-2025-09-25.png',
      timestamp: '2025-09-25T10:12:20Z',
      category: 'auth',
      description: 'Authentication page',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'Auth Flow Test'
    },
    {
      id: 'auth-2',
      name: 'OTP Login',
      path: 'otp-login-2025-09-25.png',
      url: '/screenshots/otp-login-2025-09-25.png',
      timestamp: '2025-09-25T10:13:05Z',
      category: 'auth',
      description: 'OTP login page',
      viewport: { width: 1920, height: 1080 },
      status: 'pass',
      testName: 'OTP Flow Test'
    }
  ];

  const mockTestRuns: TestRun[] = [
    {
      id: 'run-1',
      name: 'Full Test Suite',
      timestamp: '2025-09-25T17:33:00Z',
      duration: '25.4s',
      totalTests: 10,
      passed: 10,
      failed: 0,
      screenshots: mockScreenshots
    },
    {
      id: 'run-2',
      name: 'Email Feedback Test',
      timestamp: '2025-09-25T17:21:00Z',
      duration: '8.2s',
      totalTests: 3,
      passed: 2,
      failed: 1,
      screenshots: mockScreenshots.filter(s => s.category === 'email')
    }
  ];

  useEffect(() => {
    setTestRuns(mockTestRuns);
  }, []);

  const categories = [
    { id: 'all', name: 'All Tests', icon: <Globe className="w-4 h-4" /> },
    { id: 'homepage', name: 'Homepage', icon: <Monitor className="w-4 h-4" /> },
    { id: 'email', name: 'Email Tests', icon: <Mail className="w-4 h-4" /> },
    { id: 'auth', name: 'Authentication', icon: <CheckCircle className="w-4 h-4" /> },
    { id: 'features', name: 'Features', icon: <FileText className="w-4 h-4" /> }
  ];

  const filteredScreenshots = selectedCategory === 'all'
    ? mockScreenshots
    : mockScreenshots.filter(s => s.category === selectedCategory);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'pass': return 'text-green-500 bg-green-50 border-green-200';
      case 'fail': return 'text-red-500 bg-red-50 border-red-200';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'pass': return <CheckCircle className="w-4 h-4" />;
      case 'fail': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getViewportIcon = (width?: number) => {
    if (!width) return <Monitor className="w-4 h-4" />;
    if (width < 768) return <Smartphone className="w-4 h-4" />;
    if (width < 1024) return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const runNewTest = async () => {
    setIsRunningTest(true);
    // Simulate running a test
    setTimeout(() => {
      setIsRunningTest(false);
      // In production, this would trigger actual Puppeteer tests
      alert('Test suite completed! New screenshots captured.');
    }, 3000);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev'
      ? Math.max(0, currentImageIndex - 1)
      : Math.min(filteredScreenshots.length - 1, currentImageIndex + 1);

    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredScreenshots[newIndex]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                Test Results Gallery
              </h1>
              <p className="text-gray-600">
                Visual test results and screenshot captures from Puppeteer tests
              </p>
            </div>

            <button
              onClick={runNewTest}
              disabled={isRunningTest}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
                isRunningTest
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
              }`}
            >
              {isRunningTest ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Run New Test
                </>
              )}
            </button>
          </div>

          {/* Test Summary */}
          <div className="grid grid-cols-4 gap-4">
            {testRuns[0] && (
              <>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-blue-600">
                    {testRuns[0].totalTests}
                  </div>
                  <div className="text-sm text-gray-600">Total Tests</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">
                    {testRuns[0].passed}
                  </div>
                  <div className="text-sm text-gray-600">Passed</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-red-600">
                    {testRuns[0].failed}
                  </div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-purple-600">
                    {testRuns[0].duration}
                  </div>
                  <div className="text-sm text-gray-600">Duration</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {cat.icon}
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Screenshot Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredScreenshots.map((screenshot, index) => (
            <div
              key={screenshot.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all cursor-pointer transform hover:scale-105"
              onClick={() => {
                setSelectedImage(screenshot);
                setCurrentImageIndex(index);
              }}
            >
              {/* Screenshot Preview */}
              <div className="relative aspect-video bg-gray-100">
                <img
                  src={screenshot.url}
                  alt={screenshot.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmaWxsPSIjNmI3MjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSIgZm9udC1zaXplPSIxOCI+U2NyZWVuc2hvdDwvdGV4dD48L3N2Zz4=';
                  }}
                />
                <div className="absolute top-2 right-2">
                  <ZoomIn className="w-6 h-6 text-white drop-shadow-lg" />
                </div>

                {/* Status Badge */}
                <div className={`absolute top-2 left-2 px-2 py-1 rounded-full flex items-center gap-1 ${getStatusColor(screenshot.status)}`}>
                  {getStatusIcon(screenshot.status)}
                  <span className="text-xs font-medium capitalize">
                    {screenshot.status || 'pending'}
                  </span>
                </div>
              </div>

              {/* Screenshot Details */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 mb-1">
                  {screenshot.name}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {screenshot.description}
                </p>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    {getViewportIcon(screenshot.viewport?.width)}
                    <span>
                      {screenshot.viewport
                        ? `${screenshot.viewport.width}×${screenshot.viewport.height}`
                        : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {new Date(screenshot.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {screenshot.testName && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <span className="text-xs text-blue-600 font-medium">
                      {screenshot.testName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-7xl w-full">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation */}
            <button
              onClick={() => navigateImage('prev')}
              disabled={currentImageIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-12 h-12" />
            </button>

            <button
              onClick={() => navigateImage('next')}
              disabled={currentImageIndex === filteredScreenshots.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-12 h-12" />
            </button>

            {/* Image */}
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              className="w-full h-auto rounded-lg shadow-2xl"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iIzM3NDE1MSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmaWxsPSIjZTVlN2ViIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSIgZm9udC1zaXplPSIyNCI+U2NyZWVuc2hvdCBOb3QgQXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==';
              }}
            />

            {/* Image Details */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
              <h3 className="text-white text-2xl font-bold mb-2">
                {selectedImage.name}
              </h3>
              <p className="text-gray-300 mb-3">
                {selectedImage.description}
              </p>
              <div className="flex gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Camera className="w-4 h-4" />
                  {selectedImage.viewport
                    ? `${selectedImage.viewport.width}×${selectedImage.viewport.height}`
                    : 'Full Size'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(selectedImage.timestamp).toLocaleString()}
                </span>
                <a
                  href={selectedImage.url}
                  download={selectedImage.path}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Run History */}
      <div className="max-w-7xl mx-auto mt-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Test Run History</h2>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Run
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Results
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Screenshots
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {testRuns.map(run => (
                <tr key={run.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {run.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(run.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {run.duration}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                        {run.passed} passed
                      </span>
                      {run.failed > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                          {run.failed} failed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {run.screenshots.length} captures
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                      <ExternalLink className="w-4 h-4" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};